import { createServerFn } from "@tanstack/react-start";
import { supabaseAnon } from "@/lib/mcp/supabase";

/**
 * The previous site used /news-categories/<slug>, /news-author/<slug> and
 * /podcast/<slug>. Those sections live at /topics, /authors and /podcasts now,
 * so resolve each old slug to its current equivalent and redirect permanently.
 */

const parseSlug = (data: unknown): string => {
  const value = typeof data === "string" ? data : (data as { slug?: unknown })?.slug;
  if (typeof value !== "string" || !value.trim()) throw new Error("slug is required");
  return value.trim().slice(0, 300).toLowerCase();
};

const words = (slug: string): string[] =>
  slug
    .replace(/[^a-z0-9]+/gi, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean);

/** Old category slug -> current topic slug, or null when there is no topic. */
export const resolveLegacyTopic = createServerFn({ method: "GET" })
  .inputValidator(parseSlug)
  .handler(async ({ data: slug }): Promise<string | null> => {
    try {
      const db = supabaseAnon();
      const { data: exact } = await db
        .from("topic_pages")
        .select("slug")
        .eq("slug", slug)
        .limit(1)
        .maybeSingle();
      if (exact) return (exact as any).slug as string;

      // Loose match: "fintech-news" -> "fintech".
      const pattern = `%${slug.replace(/-/g, "%")}%`;
      const { data: loose } = await db
        .from("topic_pages")
        .select("slug")
        .or(`slug.ilike.${pattern},title.ilike.${pattern}`)
        .limit(1)
        .maybeSingle();
      return loose ? ((loose as any).slug as string) : null;
    } catch {
      return null;
    }
  });

/** Old author slug -> current author slug, or null. */
export const resolveLegacyAuthor = createServerFn({ method: "GET" })
  .inputValidator(parseSlug)
  .handler(async ({ data: slug }): Promise<string | null> => {
    try {
      if (slug === "byteline") return "byteline";
      const db = supabaseAnon();
      const { data: exact } = await db
        .from("authors")
        .select("slug")
        .eq("slug", slug)
        .limit(1)
        .maybeSingle();
      if (exact) return (exact as any).slug as string;

      // Bylines stored on the articles themselves still power /authors/<slug>.
      const pattern = `%${slug.replace(/-/g, "%")}%`;
      const { data: byline } = await db
        .from("articles")
        .select("id")
        .ilike("author_name", pattern)
        .limit(1)
        .maybeSingle();
      return byline ? slug : null;
    } catch {
      return null;
    }
  });

export type LegacyEpisode = { podcastId: string; episodeId: string } | null;

/** Old single-episode slug -> current podcast + episode ids, or null. */
export const resolveLegacyEpisode = createServerFn({ method: "GET" })
  .inputValidator(parseSlug)
  .handler(async ({ data: slug }): Promise<LegacyEpisode> => {
    try {
      const terms = words(slug);
      if (terms.length === 0) return null;

      const db = supabaseAnon();
      const { data } = await db
        .from("podcast_episodes")
        .select("id,podcast_id,title,episode_number")
        .eq("is_published", true)
        .limit(500);
      if (!data || data.length === 0) return null;

      let best: { row: any; score: number } | null = null;
      for (const row of data as any[]) {
        const haystack = words(
          `${row.title || ""} ${row.podcast_id || ""} ${row.episode_number ?? ""}`
        );
        const hits = terms.filter((t) => haystack.includes(t)).length;
        const score = hits / terms.length;
        if (!best || score > best.score) best = { row, score };
      }

      // Require a solid overlap so an old link never lands on the wrong episode.
      if (best && best.score >= 0.5) {
        return { podcastId: String(best.row.podcast_id), episodeId: String(best.row.id) };
      }
      return null;
    } catch {
      return null;
    }
  });
